/**
 * The local half of `wayai files pull` / `wayai files push`: the folder a file type (or one of its
 * folders) is synced into, and the record of the last pull kept beside the files.
 *
 * **The record** (`.wayai-files.json`, at the folder's root) names what the folder was pulled from
 * — base, file type, folder — and, per file, the version and content hash the local copy was last
 * in step with. Push sends that hash as `If-Match`, so an update lands only on the copy it was
 * based on, and decides from it what changed on which side. It belongs in version control with the
 * files: it is what makes a later push from another checkout version-checked too.
 *
 * Paths in the record and in the plan are POSIX paths relative to the synced folder; the remote
 * path is the folder joined to them. The base compares paths exactly, but macOS's and Windows'
 * default filesystems fold case (and Unicode normalization), so every check that decides what
 * lands on disk compares FOLDED spellings: two spellings of one local file are never treated as
 * two files.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { expected } from '../../lib/expected.js';
import { computeHash } from '../../lib/resource-files.js';
import {
  isUnder,
  readFileNoFollow,
  readRealFileOrThrow,
  requireRealSubdirNoSymlink,
  symlinkRefusal,
  writeFileNoFollow,
} from '../../lib/fs-safety.js';
import { sanitizeTerminalText } from '../../lib/terminal-output.js';

/** The record of the last pull, at the synced folder's root. */
export const RECORD_FILE = '.wayai-files.json';

/**
 * Names outside the sync in BOTH directions, matched on folded spelling: the record (and the temp
 * file it is written through), a git directory, and macOS's folder metadata. Symmetric on purpose
 * — a name push skipped but pull wrote would read as "deleted locally" and be pruned from the
 * base. Folded, because on a case-insensitive filesystem a base path `.GIT/config` IS the synced
 * folder's `.git/config`, which git executes settings from.
 */
export const OUTSIDE_SYNC: readonly string[] = [RECORD_FILE, `${RECORD_FILE}.tmp`, '.git', '.DS_Store'];
const OUTSIDE_SYNC_FOLDED = new Set(OUTSIDE_SYNC.map((n) => foldSegment(n)));

export interface SyncedFile {
  version: number;
  content_sha256: string;
}

export interface SyncRecord {
  base: string;
  file_type: string;
  /** The folder synced, without slashes at either end; `''` is the whole file type. */
  folder: string;
  files: Record<string, SyncedFile>;
}

/** A file in the local folder. */
export interface LocalFile {
  rel: string;
  content_sha256: string;
  size: number;
}

/**
 * A name as a case- and normalization-insensitive filesystem sees it. Windows also ignores a
 * trailing dot or space, so `.git.` is `.git` there.
 */
function foldSegment(segment: string): string {
  return segment.normalize('NFC').toLowerCase().replace(/[. ]+$/, '');
}

/** A relative path as a case- and normalization-insensitive filesystem sees it. */
export function foldPath(rel: string): string {
  return rel.split('/').map(foldSegment).join('/');
}

/** Whether a relative path is left out of the sync (see `OUTSIDE_SYNC`). */
export function isOutsideSync(rel: string): boolean {
  return rel.split('/').some((segment) => OUTSIDE_SYNC_FOLDED.has(foldSegment(segment)));
}

/** Whether `segments` are a usable relative path: no empty, `.` or `..` part, no backslash or NUL. */
function isCleanPath(segments: string[]): boolean {
  return segments.every((s) => s !== '' && s !== '.' && s !== '..' && !/[\\\0]/.test(s));
}

/**
 * The spelling of every folder and file a set of paths names, by folded spelling — what one
 * local directory entry is called, whichever way a path spells it.
 */
export class CaseIndex {
  private readonly bySpelling = new Map<string, string>();

  constructor(rels: Iterable<string> = []) {
    for (const rel of rels) this.add(rel);
  }

  add(rel: string): void {
    const segments = rel.split('/');
    for (let i = 1; i <= segments.length; i++) {
      const prefix = segments.slice(0, i).join('/');
      const key = foldPath(prefix);
      if (!this.bySpelling.has(key)) this.bySpelling.set(key, prefix);
    }
  }

  /**
   * The spelling already indexed for a folder or file of `rel` that differs from `rel`'s own only
   * by case or normalization — the entry `rel` would land in on a case-insensitive filesystem.
   */
  twinOf(rel: string): string | undefined {
    const segments = rel.split('/');
    for (let i = 1; i <= segments.length; i++) {
      const prefix = segments.slice(0, i).join('/');
      const known = this.bySpelling.get(foldPath(prefix));
      if (known !== undefined && known !== prefix) return known;
    }
    return undefined;
  }
}

/** `--folder` as the record keeps it: `''` for the whole file type, else a path without end slashes. */
export function normalizeFolder(raw: string | undefined): string {
  if (raw === undefined) return '';
  const folder = raw.replace(/\/+$/, '');
  if (folder === '') return '';
  if (folder.startsWith('/') || !isCleanPath(folder.split('/'))) {
    throw expected(
      `--folder must be a folder path inside the file type (e.g. hr or hr/benefits), got ${JSON.stringify(raw)}.`,
    );
  }
  return folder;
}

/** The base's path for a file of the synced folder. */
export function remotePath(folder: string, rel: string): string {
  return folder ? `${folder}/${rel}` : rel;
}

/** The local folder when `--dir` is not given: `<file_type>/<folder>` under the current directory. */
export function defaultDir(fileType: string, folder: string): string {
  return path.join(fileType, ...(folder ? folder.split('/') : []));
}

/** "base "b", file type "x", folder "y"" — how messages name a sync. */
export function describeSync(r: Pick<SyncRecord, 'base' | 'file_type' | 'folder'>): string {
  const where = r.folder ? `folder "${r.folder}"` : 'the whole file type';
  return sanitizeTerminalText(`base "${r.base}", file type "${r.file_type}", ${where}`);
}

/**
 * A Windows name that can stand for ANOTHER entry: an NTFS stream name (`.git::$INDEX_ALLOCATION`),
 * or one shaped like an 8.3 short name — at most eight characters ending in `~` and digits, with an
 * extension of at most three (`GIT~1` is `.git`'s, `NOTES~2.MD` a long `notes…md`'s). Ordinary
 * names elsewhere, so refused only there.
 */
function isWindowsAlias(segment: string): boolean {
  if (segment.includes(':')) return true;
  const dot = segment.lastIndexOf('.');
  const [stem, ext] = dot > 0 ? [segment.slice(0, dot), segment.slice(dot + 1)] : [segment, ''];
  return stem.length <= 8 && /~\d+$/.test(stem) && ext.length <= 3;
}

/**
 * Refuse base paths the folder cannot hold as files, before anything is written: a path no local
 * name can spell, a file that is also a folder, two paths a case- or normalization-insensitive
 * filesystem would write to one file or folder, or (on Windows) a name that can alias another.
 */
export function assertRepresentable(rels: string[], platform: NodeJS.Platform = process.platform): void {
  const problems: string[] = [];
  const index = new CaseIndex();
  const all = new Set(rels);
  for (const rel of rels) {
    if (!isCleanPath(rel.split('/'))) {
      problems.push(`"${rel}" cannot be a local file name`);
      continue;
    }
    if (platform === 'win32' && rel.split('/').some(isWindowsAlias)) {
      problems.push(`"${rel}" cannot be a local file name on Windows`);
      continue;
    }
    const twin = index.twinOf(rel);
    if (twin !== undefined) problems.push(`"${twin}" and "${rel}" differ only in case, so they would share one name locally`);
    index.add(rel);
    const segments = rel.split('/');
    for (let i = 1; i < segments.length; i++) {
      const parent = segments.slice(0, i).join('/');
      if (all.has(parent)) problems.push(`"${parent}" is both a file and a folder`);
    }
  }
  if (problems.length > 0) {
    throw expected(
      sanitizeTerminalText(
        `The base holds paths this folder cannot hold as files, so nothing was pulled:\n  ${problems.join('\n  ')}`,
      ),
    );
  }
}

/** The longest path a base takes for a file. */
const MAX_REMOTE_PATH = 1024;

/**
 * Refuse local files whose path the base would not keep as written, before anything is sent: one
 * the base trims (a leading or trailing space), one past its length limit, or one a pull could not
 * write back (a backslash; on Windows, a name `assertRepresentable` refuses there too).
 */
export function assertPushable(folder: string, rels: string[], platform: NodeJS.Platform = process.platform): void {
  const problems: string[] = [];
  for (const rel of rels) {
    const full = remotePath(folder, rel);
    if (full !== full.trim()) problems.push(`"${rel}" starts or ends with a space`);
    else if (full.length > MAX_REMOTE_PATH) problems.push(`"${rel}" makes a path longer than ${MAX_REMOTE_PATH} characters`);
    else if (!isCleanPath(rel.split('/'))) problems.push(`"${rel}" cannot be a file path on the base`);
    else if (platform === 'win32' && rel.split('/').some(isWindowsAlias)) {
      problems.push(`"${rel}" cannot be a file name on Windows, where it can stand for another file`);
    }
  }
  if (problems.length > 0) {
    throw expected(
      sanitizeTerminalText(`Rename these local files, so nothing was pushed:\n  ${problems.join('\n  ')}`),
    );
  }
}

/** Whether the synced folder exists at all (a symlink to one counts, so the refusal can name it). */
export function syncDirExists(dir: string): boolean {
  try {
    fs.lstatSync(dir);
    return true;
  } catch {
    return false;
  }
}

/**
 * Make the synced folder exist as a real directory — never a symlink, which would send every write
 * and delete below it somewhere else. Its own ancestors are the caller's choice of `--dir`.
 */
export function ensureSyncDir(dir: string): void {
  if (!syncDirExists(dir)) {
    fs.mkdirSync(dir, { recursive: true });
    return;
  }
  if (!fs.lstatSync(dir).isDirectory()) throw symlinkRefusal(dir, 'directory');
}

/** Read the record of the last pull, or null when the folder has none. */
export function readRecord(dir: string): SyncRecord | null {
  const text = readRealFileOrThrow(dir, path.join(dir, RECORD_FILE));
  if (text === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = undefined;
  }
  if (!isRecord(parsed)) {
    throw expected(
      `${path.join(dir, RECORD_FILE)} is not a record \`wayai files pull\` wrote. Restore it from version control, or move the folder aside and pull again.`,
    );
  }
  return parsed;
}

function isRecord(v: unknown): v is SyncRecord {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  if (typeof r.base !== 'string' || typeof r.file_type !== 'string' || typeof r.folder !== 'string') return false;
  if (typeof r.files !== 'object' || r.files === null || Array.isArray(r.files)) return false;
  return Object.values(r.files as Record<string, unknown>).every((f) => {
    const e = f as Record<string, unknown> | null;
    return (
      typeof e === 'object' &&
      e !== null &&
      typeof e.version === 'number' &&
      typeof e.content_sha256 === 'string'
    );
  });
}

/**
 * Write the record: keys in path order, through a temp file renamed over it, so a push stopped
 * mid-way leaves either the previous record or the new one — never half.
 */
export function writeRecord(dir: string, record: SyncRecord): void {
  const files: Record<string, SyncedFile> = {};
  for (const rel of Object.keys(record.files).sort()) files[rel] = syncedFile(record.files[rel]);
  const body = `${JSON.stringify({ base: record.base, file_type: record.file_type, folder: record.folder, files }, null, 2)}\n`;
  const tmp = path.join(dir, `${RECORD_FILE}.tmp`);
  if (!writeFileNoFollow(dir, tmp, Buffer.from(body, 'utf-8'))) throw symlinkRefusal(tmp, 'file');
  // `rename` replaces whatever is at the destination — a symlink included — rather than writing
  // through it.
  fs.renameSync(tmp, path.join(dir, RECORD_FILE));
}

/** The part of a file's listing (or upload answer) the record keeps. */
export function syncedFile(f: SyncedFile): SyncedFile {
  return { version: f.version, content_sha256: f.content_sha256 };
}

/** The bytes of a local file of the synced folder, read without following a symlink. */
export function readLocal(dir: string, rel: string): Buffer {
  const bytes = readFileNoFollow(dir, path.join(dir, ...rel.split('/')));
  if (!bytes) throw symlinkRefusal(path.join(dir, rel), 'file');
  return bytes;
}

/**
 * Every file under the synced folder, by relative path, with its content hash. A symlink anywhere
 * is refused: reading it would upload what it points at, and skipping it would read as a deletion.
 *
 * So is a git repository (a `.git` entry, however spelled) anywhere in the folder. Pull never
 * writes a `.git`, but Windows reaches an existing one through spellings no rule can list
 * (`GIT~1`, stream names), and git runs what a repository's config names — so a folder holding
 * one is not synced at all.
 */
export function scanLocal(dir: string): Map<string, LocalFile> {
  const out = new Map<string, LocalFile>();
  if (!syncDirExists(dir)) return out;
  ensureSyncDir(dir);
  const walk = (absDir: string, relDir: string): void => {
    for (const name of fs.readdirSync(absDir).sort()) {
      const rel = relDir ? `${relDir}/${name}` : name;
      if (foldSegment(name) === '.git') {
        throw expected(
          `${sanitizeTerminalText(path.join(dir, rel))} is a git repository inside the synced folder, which files pull and push do not sync into. ` +
            'Sync into a folder of its own — a folder inside a repository is fine; a repository\'s root, or a folder holding one, is not.',
        );
      }
      if (isOutsideSync(rel)) continue;
      const st = fs.lstatSync(path.join(absDir, name));
      if (st.isDirectory()) {
        walk(path.join(absDir, name), rel);
      } else if (st.isFile()) {
        const bytes = readLocal(dir, rel);
        out.set(rel, { rel, content_sha256: computeHash(bytes), size: bytes.length });
      } else {
        throw symlinkRefusal(path.join(dir, rel), 'file or folder');
      }
    }
  };
  walk(dir, '');
  return out;
}

/**
 * Whether a local path is still what the scan saw: absent when the scan saw nothing, else a file
 * with the scanned content. False for anything else — an edit made since must not be written over.
 */
export function matchesScan(dir: string, rel: string, scanned: LocalFile | undefined): boolean {
  const abs = path.join(dir, ...rel.split('/'));
  if (!syncDirExists(abs)) return scanned === undefined;
  if (scanned === undefined) return false;
  const bytes = readFileNoFollow(dir, abs);
  return bytes !== null && computeHash(bytes) === scanned.content_sha256;
}

/** Write a pulled file into the synced folder, never through a symlink. */
export function writeLocal(dir: string, rel: string, bytes: Uint8Array): void {
  const data = Buffer.from(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (!writeFileNoFollow(dir, path.join(dir, ...rel.split('/')), data)) {
    throw symlinkRefusal(path.join(dir, rel), 'file');
  }
}

/** Remove a local file the base deleted, then any folder that leaves empty, up to the synced folder. */
export function removeLocal(dir: string, rel: string): void {
  const abs = path.join(dir, ...rel.split('/'));
  requireRealSubdirNoSymlink(dir, path.dirname(abs), false);
  let st: fs.Stats;
  try {
    st = fs.lstatSync(abs);
  } catch {
    return;
  }
  if (!st.isFile()) throw symlinkRefusal(path.join(dir, rel), 'file');
  fs.rmSync(abs);
  // A folder left holding nothing but Finder's `.DS_Store` is empty as far as the sync goes — and
  // left behind, it would keep a case-only folder rename from landing under its new name.
  const root = path.resolve(dir);
  for (let parent = path.dirname(path.resolve(abs)); parent !== root && isUnder(root, parent); parent = path.dirname(parent)) {
    const names = fs.readdirSync(parent);
    if (names.some((n) => n !== '.DS_Store')) break;
    for (const n of names) fs.rmSync(path.join(parent, n));
    fs.rmdirSync(parent);
  }
}

/**
 * What already on disk a write of `rel` would collide with, read off the disk itself (so empty
 * folders and folders holding only names outside the sync count too):
 *   - `case_twin`: a folder or file spelled differently only by case or normalization, which IS
 *     `rel`'s name on a case-insensitive filesystem, so the write would go through it;
 *   - `file_folder`: a file where `rel` needs a folder, or a folder (`<rel>/`) where it needs a file.
 * Undefined when `rel` lands under its own name with nothing in the way.
 */
export function diskClash(dir: string, rel: string): { reason: 'case_twin' | 'file_folder'; other: string } | undefined {
  const segments = rel.split('/');
  let abs = dir;
  for (let i = 0; i < segments.length; i++) {
    let names: string[];
    try {
      names = fs.readdirSync(abs);
    } catch {
      return undefined;
    }
    const want = foldSegment(segments[i]);
    const twin = names.find((n) => n !== segments[i] && foldSegment(n) === want);
    if (twin !== undefined) return { reason: 'case_twin', other: [...segments.slice(0, i), twin].join('/') };
    if (!names.includes(segments[i])) return undefined;
    abs = path.join(abs, segments[i]);
    const isDir = fs.lstatSync(abs).isDirectory();
    const here = segments.slice(0, i + 1).join('/');
    if (i < segments.length - 1 && !isDir) return { reason: 'file_folder', other: here };
    if (i === segments.length - 1 && isDir) return { reason: 'file_folder', other: `${here}/` };
  }
  return undefined;
}
